import { useMemo } from "react";
import uniq from "lodash/uniq";
import { UIBrandedFare, UIFlightData } from "@flights/types";
import { localizationUtils, ryanairUtils } from "@flights/web-api-utils-universal";
import { useFlightOfferWithDiscount } from "hooks/useFlightOfferWithDiscount";
import { transformDisclaimersForPriceBreakdown } from "./transforms/disclaimers";
import { type BreakdownProps } from "./Breakdown/Breakdown";
import useRouteName from "hooks/useRouteName";

export type UnifiedPriceBreakdownFarePriceControllerProps = {
  fareOffer?: UIBrandedFare;
  baseOffer?: UIFlightData;
};

export function useUnifiedPriceBreakdownFarePriceController(props: UnifiedPriceBreakdownFarePriceControllerProps) {
  const { baseOffer, fareOffer } = props;
  const routeName = useRouteName();
  const segments = useMemo(() => baseOffer?.segments, [baseOffer]);
  const isHungaryDepartingRyanAir = useMemo(
    () => localizationUtils.isHungaryDepartingRyanAirFlight(segments),
    [segments]
  );
  const unifiedPriceBreakdown = useMemo(
    () => fareOffer?.unifiedPriceBreakdown || baseOffer?.unifiedPriceBreakdown,
    [baseOffer, fareOffer]
  );

  const disclaimers = useMemo(() => {
    return uniq(transformDisclaimersForPriceBreakdown(fareOffer?.priceBreakdown || baseOffer?.priceBreakdown));
  }, [fareOffer, baseOffer]);

  const { isWithDiscount } = useFlightOfferWithDiscount(baseOffer); // flights_web_unified_price_breakdown

  const isRyanair = useMemo(() => ryanairUtils.isRyanairSegments(segments), [segments]);

  const expandedByDefault = useMemo<BreakdownProps["expandedByDefault"]>(() => {
    return isRyanair
      ? {
          FLIGHT_ADULT: true,
          FLIGHT_CHILD: true,
          FLIGHT_INFANT: true,
          EXTRA: true
        }
      : undefined;
  }, [isRyanair]);

  return {
    unifiedPriceBreakdown,
    isHungaryDepartingRyanAir,
    disclaimers,
    expandedByDefault,
    isWithDiscount, // flights_web_unified_price_breakdown
    routeName
  };
}
